/**
 * Serviço para consulta de CNPJ na Receita Federal
 * Utiliza função HTTP do Firebase para evitar problemas de CORS
 */

import { getAuth } from 'firebase/auth';

const FUNCTIONS_URL = 'https://us-central1-regulamais-463618.cloudfunctions.net';

/**
 * Consulta dados de uma empresa pelo CNPJ
 * @param {string} cnpj - CNPJ da empresa (formatado ou não)
 * @returns {Promise<Object>} Dados da empresa
 */
export const consultarCNPJ = async (cnpj) => {
  try {
    // Remove formatação do CNPJ (apenas números)
    const cnpjLimpo = cnpj.replace(/\D/g, '');

    // Valida se o CNPJ tem 14 dígitos
    if (cnpjLimpo.length !== 14) {
      return {
        success: false,
        error: 'CNPJ deve ter 14 dígitos'
      };
    }

    console.log('🔍 Consultando CNPJ via função HTTP:', cnpjLimpo);

    // Obter token de autenticação
    const auth = getAuth();
    const user = auth.currentUser;

    let endpoint = `${FUNCTIONS_URL}/consultarCNPJPublico`; // Função pública por padrão
    let headers = {
      'Content-Type': 'application/json'
    };

    // Se usuário estiver autenticado, usar função autenticada
    if (user) {
      try {
        const token = await user.getIdToken();
        endpoint = `${FUNCTIONS_URL}/consultarCNPJReceita`;
        headers['Authorization'] = `Bearer ${token}`;
        console.log('🔐 Usando função autenticada para usuário logado');
      } catch (tokenError) {
        console.warn('⚠️ Erro ao obter token, usando função pública:', tokenError);
        // Continua com função pública se houver erro no token
      }
    } else {
      console.log('🌐 Usando função pública para usuário não autenticado');
    }

    // Fazer requisição para a função HTTP
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ cnpj: cnpjLimpo })
    });

    const result = await response.json();

    if (response.ok) {
      console.log('✅ Dados do CNPJ recebidos via função HTTP:', result);
      return {
        success: true,
        data: result.data
      };
    } else {
      console.error('❌ Erro na resposta da função:', result);
      return {
        success: false,
        error: result.error || 'Erro ao consultar CNPJ'
      };
    }

  } catch (error) {
    console.error('❌ Erro ao consultar CNPJ:', error);

    return {
      success: false,
      error: error.message || 'Erro ao consultar CNPJ'
    };
  }
};

/**
 * Valida se um CNPJ é válido
 * @param {string} cnpj - CNPJ para validar
 * @returns {boolean} True se válido
 */
export const validarCNPJ = (cnpj) => {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  
  if (cnpjLimpo.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(cnpjLimpo)) return false;
  
  // Validação dos dígitos verificadores
  let tamanho = cnpjLimpo.length - 2;
  let numeros = cnpjLimpo.substring(0, tamanho);
  let digitos = cnpjLimpo.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado !== parseInt(digitos.charAt(0))) return false;
  
  tamanho = tamanho + 1;
  numeros = cnpjLimpo.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  return resultado === parseInt(digitos.charAt(1));
};
